import React, { useEffect, useMemo, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  consultationApi,
  formatConsultDate,
  formatFee,
} from '../../api/consultationApi';
import '../../styles/consultation.css';

const STEPS = ['Duration', 'Schedule', 'Review', 'Payment', 'Confirmed'];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const BookingModal = ({ advisor, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [availability, setAvailability] = useState(null);
  const [duration, setDuration] = useState(30);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [instant, setInstant] = useState(false);
  const [booking, setBooking] = useState(null);

  const fetchAvailability = async (nextDuration) => {
    setLoading(true);
    setError('');
    try {
      const res = await consultationApi.getAvailability(advisor._id, {
        type: 'video',
        duration: nextDuration,
        days: 14,
      });
      setAvailability(res.data);
      const firstDate = res.data.dates?.[0]?.date || '';
      setSelectedDate((prev) => (res.data.dates?.some((d) => d.date === prev) ? prev : firstDate));
      setSelectedSlot(null);
      setInstant(false);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to load consultation availability');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailability(duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisor._id]);

  const slotsForDate = useMemo(() => {
    return availability?.dates?.find((d) => d.date === selectedDate)?.slots || [];
  }, [availability, selectedDate]);

  const handleDuration = async (value) => {
    setDuration(value);
    await fetchAvailability(value);
  };

  const createBooking = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await consultationApi.createBooking({
        advocateId: advisor._id,
        durationMinutes: duration,
        instant,
        scheduledStart: instant ? undefined : selectedSlot?.start,
      });
      setBooking(res.data.booking);
      if (res.data.paymentRequired) {
        setStep(3);
      } else {
        setStep(4);
        toast.success('Video consultation booked');
      }
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to create the consultation booking');
    } finally {
      setSubmitting(false);
    }
  };

  const pay = async () => {
    if (!booking) return;
    setSubmitting(true);
    setError('');
    try {
      const orderRes = await consultationApi.createPaymentOrder(booking._id);
      const order = orderRes.data;
      if (!order.required || order.alreadyPaid) {
        const refreshed = await consultationApi.getBooking(booking._id);
        setBooking(refreshed.data.booking);
        setStep(4);
        return;
      }

      if (order.mockCheckout || order.provider === 'internal') {
        const paid = await consultationApi.confirmInternalPayment(booking._id);
        setBooking(paid.data.booking);
        setStep(4);
        toast.success('Consultation confirmed');
        return;
      }

      const ready = await loadRazorpayScript();
      if (!ready) throw new Error('Unable to load the payment gateway');

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'ULJAAPP Video Consult',
        description: `Consultation with Adv. ${advisor.firstName} ${advisor.lastName}`,
        order_id: order.orderId,
        handler: async (response) => {
          try {
            const verified = await consultationApi.verifyPayment(booking._id, response);
            setBooking(verified.data.booking);
            setStep(4);
            toast.success('Payment successful. Consultation booked.');
          } catch (err) {
            setError(err.response?.data?.msg || 'Payment verification failed');
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
        theme: { color: '#00bfae' },
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Payment could not be started');
      setSubmitting(false);
    }
  };

  const canJoin = booking?.joinableNow;

  return (
    <div className="consult-modal-overlay" onClick={onClose}>
      <div className="consult-modal" onClick={(e) => e.stopPropagation()}>
        <div className="consult-modal-header">
          <div>
            <h3>Video Consult</h3>
            <div className="consult-muted">
              with Adv. {advisor.firstName} {advisor.lastName}
            </div>
          </div>
          <button className="consult-modal-close" onClick={onClose} aria-label="Close">
            <IoMdClose size={22} />
          </button>
        </div>

        <div className="consult-steps">
          {STEPS.map((label, idx) => (
            <span
              key={label}
              className={`consult-step-pill ${idx === step ? 'active' : ''} ${idx < step ? 'done' : ''}`}
            >
              {idx + 1}. {label}
            </span>
          ))}
        </div>

        {error && <div className="consult-error">{error}</div>}

        {loading ? (
          <p>Loading availability…</p>
        ) : !availability?.enabled ? (
          <p className="consult-muted">
            This advocate is not currently offering video consultations.
          </p>
        ) : (
          <>
            {step === 0 && (
              <>
                <label className="consult-label">Consultation duration</label>
                <div className="consult-duration-row">
                  {(availability.durations || [15, 30, 45, 60]).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`consult-chip ${duration === d ? 'selected' : ''}`}
                      onClick={() => handleDuration(d)}
                    >
                      {d} min
                    </button>
                  ))}
                </div>
                <div className="consult-actions">
                  <button className="consult-btn secondary" type="button" onClick={onClose}>Cancel</button>
                  <button className="consult-btn primary" type="button" onClick={() => setStep(1)}>
                    Continue
                  </button>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                {availability.availableNow && (
                  <div className="consult-available-now">
                    <strong>Available now</strong>
                    <p className="consult-muted" style={{ margin: '6px 0 10px' }}>
                      Adv. {advisor.firstName} can take a video consultation immediately.
                    </p>
                    <button
                      type="button"
                      className={`consult-chip ${instant ? 'selected' : ''}`}
                      onClick={() => { setInstant(true); setSelectedSlot(null); }}
                    >
                      Consult now
                    </button>
                  </div>
                )}

                <label className="consult-label">
                  {availability.availableNow ? 'Or choose a later slot' : 'Choose a date and time'}
                </label>
                {(availability.dates || []).length === 0 && !availability.availableNow ? (
                  <p className="consult-muted">No consultation slots are available in the next 14 days.</p>
                ) : (
                  <>
                    <div className="consult-date-list">
                      {(availability.dates || []).map((d) => {
                        const dateObj = new Date(`${d.date}T00:00:00`);
                        return (
                          <button
                            type="button"
                            key={d.date}
                            className={`consult-date-btn ${selectedDate === d.date && !instant ? 'selected' : ''}`}
                            onClick={() => { setSelectedDate(d.date); setInstant(false); setSelectedSlot(null); }}
                          >
                            <span className="m">{dateObj.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                            <span className="d">{dateObj.getDate()}</span>
                            <span className="m">{dateObj.toLocaleDateString('en-IN', { month: 'short' })}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="consult-slot-grid">
                      {slotsForDate.map((slot) => (
                        <button
                          type="button"
                          key={slot.start}
                          className={`consult-chip ${selectedSlot?.start === slot.start && !instant ? 'selected' : ''}`}
                          onClick={() => { setSelectedSlot(slot); setInstant(false); }}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <div className="consult-actions">
                  <button className="consult-btn secondary" type="button" onClick={() => setStep(0)}>Back</button>
                  <button
                    className="consult-btn primary"
                    type="button"
                    disabled={!instant && !selectedSlot}
                    onClick={() => setStep(2)}
                  >
                    Continue
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="consult-summary">
                  <div className="consult-summary-row">
                    <span>Advocate</span>
                    <span>Adv. {advisor.firstName} {advisor.lastName}</span>
                  </div>
                  <div className="consult-summary-row">
                    <span>When</span>
                    <span>{instant ? 'Immediate consultation' : formatConsultDate(selectedSlot?.start, availability.timezone)}</span>
                  </div>
                  <div className="consult-summary-row">
                    <span>Duration</span>
                    <span>{duration} minutes</span>
                  </div>
                  <div className="consult-summary-row total">
                    <span>Consultation fee</span>
                    <span>{formatFee(availability.fee, availability.currency)}</span>
                  </div>
                </div>
                {availability.requireAcceptance && (
                  <p className="consult-muted">The advocate will confirm this request after payment.</p>
                )}
                <div className="consult-actions">
                  <button className="consult-btn secondary" type="button" onClick={() => setStep(1)}>Back</button>
                  <button className="consult-btn primary" type="button" disabled={submitting} onClick={createBooking}>
                    {submitting ? 'Booking…' : (availability.fee > 0 ? 'Proceed to payment' : 'Confirm booking')}
                  </button>
                </div>
              </>
            )}

            {step === 3 && booking && (
              <>
                <div className="consult-summary">
                  <div className="consult-summary-row">
                    <span>Amount payable</span>
                    <strong>{formatFee(booking.fee, booking.currency)}</strong>
                  </div>
                </div>
                <p className="consult-muted">Complete payment to confirm this video consultation.</p>
                <div className="consult-actions">
                  <button className="consult-btn secondary" type="button" onClick={onClose}>Cancel</button>
                  <button className="consult-btn primary" type="button" disabled={submitting} onClick={pay}>
                    {submitting ? 'Processing…' : `Pay ${formatFee(booking.fee, booking.currency)}`}
                  </button>
                </div>
              </>
            )}

            {step === 4 && booking && (
              <>
                <div className="consult-summary">
                  <div className="consult-summary-row">
                    <span>Status</span>
                    <span>{booking.displayStatus}</span>
                  </div>
                  <div className="consult-summary-row">
                    <span>Payment</span>
                    <span>{booking.paymentStatus}</span>
                  </div>
                  <div className="consult-summary-row">
                    <span>Advocate</span>
                    <span>Adv. {advisor.firstName} {advisor.lastName}</span>
                  </div>
                  <div className="consult-summary-row">
                    <span>When</span>
                    <span>{formatConsultDate(booking.scheduledStart, booking.timezone)}</span>
                  </div>
                  <div className="consult-summary-row">
                    <span>Duration</span>
                    <span>{booking.durationMinutes} minutes</span>
                  </div>
                  <div className="consult-summary-row total">
                    <span>Fee</span>
                    <span>{formatFee(booking.fee, booking.currency)}</span>
                  </div>
                </div>
                <div className="consult-actions">
                  <button className="consult-btn secondary" type="button" onClick={onClose}>Close</button>
                  {canJoin && (
                    <button
                      className="consult-btn primary"
                      type="button"
                      onClick={() => navigate(`/video-consultation/${booking._id}`)}
                    >
                      Join Video Consultation
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
